import * as React from "react";
import * as _ from "lodash";
import * as S from "@styled"
import { connect } from "react-redux";

import { ParameterViewer } from "../../processDetails/ParameterViewer";
import { runTestProcess, testProcessCanceled } from "../actions/TestProcessActions";
import { ElementType, IProcessVariable, ITaskData } from "../model/IProcessData";
import { State } from "../model/State";
import { default as PollingComponent } from "./PollingComponent";
import { default as TaskEditor } from "./TaskEditor";

import AgentGroupSelectMenu from "../../shared/components/AgentGroupSelectMenu";
import { AgentGroup, defaultAgentGroupName, defaultEnvironmentName } from "../../shared/model/AgentGroup";
import { DispatchType, Utilities } from "../../shared/utilities";
import { addOrSelectTestPanel } from "../actions/SidePanelActions";
import { ITestProcessExecution } from "../reducers/TestProcessReducer";
import { ICompletionTypes } from "../../shared/monaco/model/model";
import { loader } from "@monaco-editor/react";
import { languageProvider } from "../../shared/monaco/lang/languageProvider";
import { IDisposable } from "monaco-editor";
import { UserSnippet } from "../actions/SnippetActions";
import { updateTestTaskTargetFramework } from "../actions/UpdateTargetFramework";
import { Tab, TabList, TabPanel } from "react-tabs";
import { Line } from "rc-progress";
import styled, { keyframes } from "styled-components";
import colors from "shared/styles/colors";

enum TestTabType {
    Input,
    Results
}

const linebackgroundAnimation = keyframes`
    0% {
        stroke: ${colors.purplePrimary};
    }
    25% {
        stroke: ${colors.orangePrimary};
    }
    50% {
        stroke: ${colors.pinkPrimary};
    }
    75% {
        stroke: ${colors.bluePrimary};
    }
    100% {
        stroke: ${colors.purplePrimary};
    }
`

const AnimatingLine = styled(Line)`
    .rc-progress-line-path {
        stroke: ${colors.purplePrimary};
        animation: 2s ${linebackgroundAnimation} infinite;
    }
`

export class TestProcessPanel extends React.Component<TestProcessPanelProps, TestProcessPanelState> {

    _isMounted = false;
    disposableLanguageProvider: IDisposable | null = null;

    constructor(props: TestProcessPanelProps) {
        super(props);
        this.state = {
            activeTab: TestTabType.Input,
            currentItem: 0,
            selectedAgentGroup: defaultAgentGroupName,
            selectedEnvironment: defaultEnvironmentName,
            language: ""
        };
    }

    public componentDidMount() {
        this._isMounted = true;
        this.updateCompletionData();
    }

    public componentWillUnmount(): void {
        this.disposeLanguageProvider()
        this._isMounted = false;
    }

    public componentDidUpdate(prevProps: TestProcessPanelProps, prevState: TestProcessPanelState): void {
        if (this.state.language !== prevState.language) {
            this.updateCompletionData();
        }
    }

    public disposeLanguageProvider(): void {
        if (this.disposableLanguageProvider !== null) {
            this.disposableLanguageProvider.dispose()
            this.disposableLanguageProvider = null;
        }
    }

    public setLang(language: string): void {
        if (this._isMounted) {
            this.setState({ ...this.state, language })
        }
    }

    public async updateCompletionData(): Promise<void> {
        this.disposeLanguageProvider();
        const completionTypes: ICompletionTypes = {
            environmentVariables: this.props.environmentVariables,
            processReferences: [],
            returnVariables: [],
            triggers: [],
            variables: [],
            snippets: this.props.snippets,
            processVariables: this.props.processVariables.map(pv => pv.name),
            apiSpecPaths: [],
        }
        try {
            const initializedMonaco = await loader.init();
            if (this.disposableLanguageProvider === null) {
                this.disposableLanguageProvider = languageProvider.registerLanguageProvider(initializedMonaco, completionTypes, this.setLang.bind(this));
            }
        } catch (e) {
            console.error(e)
        }
    }

    public render() {

        const targetFramework = this.props.agentGroups.find((ag) => ag.name === this.state.selectedAgentGroup)?.isCrossPlatform
            ? "net8.0" // "netstandard20"
            : "net471";

        const selectedTaskNotCompatible = !this.props.tasks
            .find((t) => t.uri === this.props.selectedTestTaskUri
                && (targetFramework === "net471" || t.isStandard || t.isDotNet));

        return (
            <S.ParameterFieldset>
                <S.FormGroup>
                    <S.Label>
                        {FRENDS.Localizations.ExecuteInSelectedAg}
                    </S.Label>
                    <AgentGroupSelectMenu
                        agentGroups={this.props.agentGroups}
                        currentAgentGroupName={this.state.selectedAgentGroup}
                        onChangeAgentGroup={this.handleAgentGroupChange}
                        showManageLink={false}
                        disabledAgentGroupNames={this.props.disabledAgentGroupNames}
                        disabledTitleText="Test process exectution not allowed for Agent group"
                    />
                </S.FormGroup>
                <S.FormGroup>
                    <S.ButtonGroup gap={12}>
                        <S.Button
                            id="run-test-process"
                            disabled={this.props.isRunningProcess || !this.props.selectedTestTaskUri || selectedTaskNotCompatible}
                            onClick={this.handleRunTest}
                        >
                            {FRENDS.Localizations.Run}
                        </S.Button>
                        <S.Button variant={S.ButtonType.bordered} disabled={!this.props.isRunningProcess} onClick={this.handleStopTest}>{FRENDS.Localizations.Stop}</S.Button>
                    </S.ButtonGroup>
                </S.FormGroup>
                {/*Hide for now, figure out a better UI to to jump between test executions. State contains all relevant data
                <ButtonGroup>
                    <Button onClick={this.selectNextResult} disabled={this.state.currentItem === 0 || this.props.isRunningProcess}>
                        {FRENDS.Localizations.Next}
                    </Button>
                    <Button onClick={this.selectPreviousResult} disabled={!this.props.testExecutions.length || (this.props.testExecutions.length - 1) === this.state.currentItem || this.props.isRunningProcess}>
                        {FRENDS.Localizations.Previous}
                    </Button>
                </ButtonGroup>*/}

                {this.props.isRunningProcess && <AnimatingLine percent={100} strokeWidth={2} />}
                <hr></hr>
                <S.NonSolidTabs
                    id="TestProcessPanelTabs"
                    className="custom-tabs"
                    selectedIndex={this.state.activeTab}
                    onSelect={this.handleTabSelect}
                    data-test-execution-id={this.props.executionId}
                >
                    <PollingComponent testProcessExecutionId={this.props.executionId} onComplete={this.handlePollingComplete} onFailureToStartProcess={this.handeleProcessFailedToStart} environmentName={this.state.selectedEnvironment}>
                        <TabList>
                            <Tab id="tab-input">
                                {FRENDS.Localizations.Parameters}
                            </Tab>
                            <Tab id="tab-results">
                                {FRENDS.Localizations.Result}
                            </Tab>
                        </TabList>
                        <TabPanel>
                        <TaskEditor id={this.props.selectedElementId} isTest={true} targetFramework={targetFramework} />
                        </TabPanel>
                        <TabPanel>
                            {this.props.testExecutions.length && this.props.testExecutions[this.state.currentItem].result != null ?
                                <ParameterViewer
                                    id={"TaskNode"}
                                    stepName={"TaskNode"}
                                    step={this.props.testExecutions[this.state.currentItem].result}
                                    onLoopIndexChangeClick={null}
                                    isExecutionType={false}
                                    currentIndexes={null}
                                    maxLoopIndex={null}
                                    elementType={ElementType.Task}
                                    elementParameters={null}
                                    taskDetails={null}
                                    subprocessExecutionId={null}
                                    environmentName={this.state.selectedEnvironment}
                                />
                                : <div>{FRENDS.Localizations.NoInstancesFound}</div>
                            }
                        </TabPanel>
                        </PollingComponent>
                </S.NonSolidTabs>
            </S.ParameterFieldset>
        );
    }

    private handleAgentGroupChange = (environmentName: string, agentGroupName: string) => {
        const newSelectedAgentGroup = this.props.agentGroups.find((x) => x.name === agentGroupName);
        if (newSelectedAgentGroup) {
            this.props.onAgentGroupChanged(newSelectedAgentGroup?.isCrossPlatform || false)

        }
        this.setState({ selectedEnvironment: environmentName, selectedAgentGroup: agentGroupName });
    }

    private handeleProcessFailedToStart = () => {
        this.handleStopTest();
        Utilities.toastError(FRENDS.Localizations.TestProcess_FailedToStart);
    }

    private handlePollingComplete = () => {
        this.setState({ currentItem: 0, activeTab: TestTabType.Results });
    }

    private handleTabSelect = (selectedTab: any) => {
        this.setState({ activeTab: selectedTab });
    }

    private selectNextResult = () => {
        this.setResult(this.state.currentItem - 1);
    }

    private selectPreviousResult = () => {
        this.setResult(this.state.currentItem + 1);
    }

    private setResult(newIndex: number) {
        this.props.changeTest(this.props.testExecutions[newIndex].selectedTaskId, this.props.testExecutions[newIndex].parameters);
        this.setState({ currentItem: newIndex });
    }

    private handleRunTest = () => {
        this.props.runTest(this.state.selectedAgentGroup);
    }

    private handleStopTest = () => {
        this.props.onStopFetch(this.props.executionId);
    }
}

interface TestProcessPanelState {
    activeTab: TestTabType;
    currentItem: number;
    selectedAgentGroup: string;
    selectedEnvironment: string;
    language: string;
}

interface TestProcessPanelOwnProps {
    selectedElementId: string;
}

interface TestProcessPanelStateProps {
    selectedTestTaskUri: string | undefined;
    testExecutions: ITestProcessExecution[];
    executionId: string | null;
    isRunningProcess: boolean;
    environmentVariables: string[];
    agentGroups: AgentGroup[];
    disabledAgentGroupNames: string[];
    tasks: ITaskData[];
    snippets: UserSnippet[];
    processVariables: IProcessVariable[];
}

interface TestProcessPanelDispatchProps {
    runTest: (selectedAgentGroup: string) => void;
    onStopFetch: (testExecutionId: string | null) => void;
    changeTest: (selectedTaskId: string, parameters: any) => void;
    onAgentGroupChanged: (isCrossPlatform: boolean) => void;
}

export type TestProcessPanelProps = TestProcessPanelOwnProps & TestProcessPanelStateProps & TestProcessPanelDispatchProps;
const mapStateToProps = (state: State, ownProps: TestProcessPanelProps): TestProcessPanelStateProps => {
    return {
        selectedTestTaskUri: state.elements[ownProps.selectedElementId].selectedTypeId,
        executionId: state.testProcess.testExecutionId,
        isRunningProcess: state.testProcess.testExecutionId != null,
        testExecutions: state.testProcess.executions,
        environmentVariables: state.environmentVariables,
        agentGroups: state.agentGroups,
        disabledAgentGroupNames: state.agentGroups.filter((ag) => !ag.allowRunningTestProcesses).map((x) => x.name),
        tasks: state.tasks,
        snippets: state.snippets,
        processVariables: state.processVariables,
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>, ownProps: TestProcessPanelOwnProps): TestProcessPanelDispatchProps => {
    return {
        runTest: (selectedAgentGroup: string) => dispatch(runTestProcess(selectedAgentGroup)),
        onStopFetch: (testExecutionId) => dispatch(testProcessCanceled(testExecutionId)),
        changeTest: (selectedTaskId: string, parameters: any) => dispatch(addOrSelectTestPanel(parameters, selectedTaskId)),
        onAgentGroupChanged: (isCrossPlatform: boolean) => dispatch(updateTestTaskTargetFramework(isCrossPlatform))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TestProcessPanel);
